import { useEffect, useRef, useState } from 'react'

interface WatermarkPreviewProps {
  baseImageUrl?: string
  watermarkUrl: string | null
  posX: number
  posY: number
  picOriginX: number
  picOriginY: number
  sizeRatio: number
  rotation: number
  opacity: number
  editable?: boolean
  onPositionChange?: (x: number, y: number) => void
  className?: string
}

const DEFAULT_BASE = '/demoX/wedding/4553 Large.jpeg'

export default function WatermarkPreview({
  baseImageUrl = DEFAULT_BASE,
  watermarkUrl,
  posX,
  posY,
  picOriginX,
  picOriginY,
  sizeRatio,
  rotation,
  opacity,
  editable = false,
  onPositionChange,
  className,
}: WatermarkPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const baseImgRef = useRef<HTMLImageElement>(null)
  const wmImgRef = useRef<HTMLImageElement>(null)
  const [baseSize, setBaseSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 })
  const [wmNatural, setWmNatural] = useState<{ w: number; h: number } | null>(null)

  useEffect(() => {
    const el = baseImgRef.current
    if (!el) return
    const updateSize = () => setBaseSize({ w: el.clientWidth, h: el.clientHeight })
    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(el)
    return () => observer.disconnect()
  }, [baseImageUrl])

  function handleBaseLoad() {
    const el = baseImgRef.current
    if (!el) return
    setBaseSize({ w: el.clientWidth, h: el.clientHeight })
  }

  function handleWmLoad() {
    const el = wmImgRef.current
    if (!el) return
    setWmNatural({ w: el.naturalWidth, h: el.naturalHeight })
  }

  function handleClick(e: React.MouseEvent<HTMLDivElement>) {
    if (!editable || !onPositionChange) return
    const baseEl = baseImgRef.current
    if (!baseEl) return
    const rect = baseEl.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height
    onPositionChange(Math.max(0, Math.min(1, x)), Math.max(0, Math.min(1, y)))
  }

  let wmStyle: React.CSSProperties | null = null
  if (watermarkUrl && wmNatural && baseSize.w > 0 && baseSize.h > 0) {
    const shorter = Math.min(baseSize.w, baseSize.h)
    const longest = sizeRatio * shorter
    const wmAspect = wmNatural.w / wmNatural.h
    let wmW: number
    let wmH: number
    if (wmAspect >= 1) {
      wmW = longest
      wmH = longest / wmAspect
    } else {
      wmH = longest
      wmW = longest * wmAspect
    }
    wmStyle = {
      position: 'absolute',
      width: `${wmW}px`,
      height: `${wmH}px`,
      left: `${posX * 100}%`,
      top: `${posY * 100}%`,
      transform: `translate(${-picOriginX * 100}%, ${-picOriginY * 100}%) rotate(${-rotation}deg)`,
      transformOrigin: `${picOriginX * 100}% ${picOriginY * 100}%`,
      opacity,
      pointerEvents: 'none',
      userSelect: 'none',
    }
  }

  return (
    <div
      ref={containerRef}
      className={`relative inline-block ${editable ? 'cursor-crosshair' : ''} ${className || ''}`}
      onClick={handleClick}
    >
      <img
        ref={baseImgRef}
        src={baseImageUrl}
        alt="Vorschau"
        className="object-contain max-h-[60vh] max-w-full block"
        onLoad={handleBaseLoad}
        draggable={false}
      />
      {watermarkUrl && (
        <img
          ref={wmImgRef}
          key={watermarkUrl}
          src={watermarkUrl}
          alt="Wasserzeichen"
          onLoad={handleWmLoad}
          style={wmStyle ?? { display: 'none' }}
          draggable={false}
        />
      )}
      {editable && watermarkUrl && (
        <div
          className="absolute w-3 h-3 border-2 border-primary rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none shadow-[0_0_0_2px_rgba(0,0,0,0.4)]"
          style={{ left: `${posX * 100}%`, top: `${posY * 100}%` }}
        />
      )}
    </div>
  )
}
